import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, Facebook, Instagram, Youtube } from "lucide-react";
import { Reveal } from "@/components/anim/Reveal";

const LogoMark = () => (
  <span className="inline-flex items-center gap-2">
    <svg width="28" height="28" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path d="M4 4h13v6H10v7H4V4z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
      <path d="M36 4H23v6h7v7h6V4z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
      <path d="M4 36h13v-6H10v-7H4v13z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
      <path d="M36 36H23v-6h7v-7h6v13z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
    </svg>
    <span
      className="font-bold tracking-[0.28em] text-[11px] px-2 py-1 rounded-md"
      style={{
        color: "#4dd7ee",
        background: "rgba(77,215,238,0.08)",
        border: "1px solid rgba(77,215,238,0.28)",
      }}
    >
      FOUR SQUARE
    </span>
  </span>
);

const socialCls =
  "w-9 h-9 rounded-md border border-[rgba(77,215,238,0.22)] inline-flex items-center justify-center text-[color:var(--text-dim)] hover:text-[color:var(--cyan-bright)] hover:border-[rgba(77,215,238,0.55)] hover:-translate-y-0.5 transition-all duration-300";

const Footer = () => {
  return (
    <footer
      className="relative pt-16 pb-8 mt-6"
      data-testid="site-footer"
      style={{ borderTop: "1px solid rgba(77,215,238,0.12)" }}
    >
      <div className="fs-container">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div>
              <LogoMark />
              <p className="text-[color:var(--text-dim)] mt-5 text-sm leading-relaxed max-w-xs">
                Enterprise-grade business communication, connectivity and cyber
                security solutions — engineered for growing organisations.
              </p>
              <div className="flex gap-3 mt-6" data-testid="footer-socials">
                <a href="#" aria-label="LinkedIn" className={socialCls}><Linkedin size={16} /></a>
                <a href="#" aria-label="Facebook" className={socialCls}><Facebook size={16} /></a>
                <a href="#" aria-label="Instagram" className={socialCls}><Instagram size={16} /></a>
                <a href="#" aria-label="YouTube" className={socialCls}><Youtube size={16} /></a>
              </div>
            </div>

            <div>
              <div className="fs-tag mb-4">Company</div>
              <ul className="space-y-3 text-sm">
                {[
                  ["Home", "/"],
                  ["About", "/about"],
                  ["Services", "/services"],
                  ["Industries", "/industries"],
                  ["Contact", "/contact"],
                ].map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-[color:var(--text-dim)] hover:text-[color:var(--cyan-bright)] transition-colors"
                      data-testid={`footer-link-${label.toLowerCase()}`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="fs-tag mb-4">Solutions</div>
              <ul className="space-y-3 text-sm">
                {[
                  "WhatsApp Business",
                  "Business True Caller",
                  "Network",
                ].map((s) => (
                  <li key={s}>
                    <Link
                      to="/services"
                      className="text-[color:var(--text-dim)] hover:text-[color:var(--cyan-bright)] transition-colors"
                    >
                      {s}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="fs-tag mb-4">Contact</div>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3 text-[color:var(--text-dim)]">
                  <Phone size={16} className="mt-0.5 text-[color:var(--cyan)]" />
                  <a href="tel:+918681833444" className="hover:text-[color:var(--cyan-bright)]">
                    +91 86818 33444
                  </a>
                </li>
                <li className="flex items-start gap-3 text-[color:var(--text-dim)]">
                  <Mail size={16} className="mt-0.5 text-[color:var(--cyan)]" />
                  <a
                    href="mailto:connect@foursquareinfo.com"
                    className="hover:text-[color:var(--cyan-bright)] break-all"
                  >
                    connect@foursquareinfo.com
                  </a>
                </li>
                <li className="flex items-start gap-3 text-[color:var(--text-dim)]">
                  <MapPin size={16} className="mt-0.5 text-[color:var(--cyan)]" />
                  <span>
                    Square Space Business Centre,
                    <br />
                    No. 37, Govindan Street,
                    <br />
                    Ayyavoo Naidu Colony,
                    <br />
                    Aminjikarai,
                    <br />
                    Chennai – 600029,
                    <br />
                    Tamil Nadu, India
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </Reveal>

        <div className="fs-divider mt-14 mb-6" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-[color:var(--text-muted)]">
          <span>© 2026 Four Square. All rights reserved.</span>
          <span>Maintained by <a href="https://www.wibowtechnology.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--cyan-bright)]">Wibow Technology</a></span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
